import React from 'react';
import {
  InnerPageHeader, SegmentedControl, Button, Badge, EmptyState, SkeletonText,
} from '@parsina/framework';
import { UserX, Stethoscope, MessagesSquare } from 'lucide-react';

import { CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { PracticeBanner, PracticeBadge } from '../../components/common/PracticeNotice.jsx';
import ErrorState from '../../components/common/ErrorState.jsx';
import { ageLabel, patientName } from '../../lib/caseForm.js';
import { CaseFormDialog, DeletePatientDialog } from '../Patients/PatientDialogs.jsx';
import StartVisitDialog from './StartVisitDialog.jsx';
import RecordBody, { RecordMenu } from './RecordBody.jsx';
import {
  RECORD_TABS, TAB_LABEL_KEYS, headerChips, startVisitLabelKey,
} from './PatientRecord.shared.js';

// The patient record on a desktop. core.md §7.4.
//
// One header, one tab strip, one reading column. The tab is a URL SEGMENT, so a
// refresh, a back button and a pasted link all restore the part of the record
// the student was reading — which is why the strip reports its choice upward
// rather than holding it.

export default function PatientRecordWeb({ screen, tab, onTab, caseId }) {
  const { t, formatNumber, formatDate } = useTranslation();
  const { caseRow } = screen;

  if (screen.loading) {
    return (
      <div style={{ padding: 20 }} data-testid="record-loading">
        <SkeletonText width="40%" height={22} />
        <div style={{ marginTop: 16 }}><SkeletonText width="100%" height={180} /></div>
      </div>
    );
  }

  if (screen.error) {
    return (
      <div style={{ padding: 20 }}>
        <ErrorState error={screen.error} onRetry={screen.reload} data-testid="record-error" />
      </div>
    );
  }

  // «این بیمار پیدا نشد» — the SAME screen for a patient that never existed and
  // one belonging to another student (core.md §3.2). No retry: there is nothing
  // to retry, and a retry button here would be an oracle a student could hold
  // down to find out which ids exist.
  if (screen.notFound) {
    return (
      <EmptyState
        icon={<UserX size={44} strokeWidth={1.5} aria-hidden />}
        title={t('record.notFound.title')}
        description={t('record.notFound.body')}
        action={
          <Button variant="outline" onClick={screen.back} data-testid="record-not-found-back">
            {t('record.notFound.action')}
          </Button>
        }
        data-testid="record-not-found"
      />
    );
  }

  const chips = headerChips(caseRow, { t, formatNumber, formatDate, ageLabel });

  return (
    <div data-testid="record-web">
      <InnerPageHeader
        onBack={screen.back}
        backLabel={t('common.actions.back')}
        title={patientName(caseRow, t)}
        badges={(
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            {/* core.md §3.1: every screen that names a patient carries it. */}
            <PracticeBadge />
            {screen.archived && (
              <Badge variant="outline" data-testid="record-archived">
                {t('record.header.archived')}
              </Badge>
            )}
          </span>
        )}
        meta={chips.map((chip) => (
          <span key={chip.id} data-testid={`record-chip-${chip.id}`}>{chip.label}</span>
        ))}
      >
        <Button
          onClick={screen.startVisit}
          disabled={screen.archived}
          title={screen.archived ? t('record.header.archivedHint') : undefined}
          data-testid="record-start-visit"
        >
          <Stethoscope size={15} aria-hidden />
          {t(startVisitLabelKey(caseRow))}
        </Button>
        <Button variant="outline" onClick={() => screen.openChat()} data-testid="record-chat">
          <MessagesSquare size={15} aria-hidden />
          {t('record.actions.chat')}
        </Button>
        <RecordMenu screen={screen} />
      </InnerPageHeader>

      <div style={{ padding: '16px 20px 32px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        <PracticeBanner />

        <SegmentedControl
          options={RECORD_TABS.map((id) => ({ value: id, label: t(TAB_LABEL_KEYS[id]) }))}
          value={tab}
          onChange={onTab}
          ariaLabel={t('record.tabs.overview')}
          data-testid="record-tabs"
        />

        {screen.archived && (
          <p style={{ margin: 0, fontSize: 12.5, color: CT.mute }}>
            {t('record.header.archivedHint')}
          </p>
        )}

        <RecordBody tab={tab} screen={screen} caseId={caseId} />
      </div>

      <StartVisitDialog
        open={screen.startOpen}
        onClose={screen.closeStart}
        reason={screen.reason}
        onReason={screen.setReason}
        onSubmit={screen.submitStart}
        saving={screen.starting}
      />
      <CaseFormDialog
        open={screen.editOpen}
        onClose={screen.closeEdit}
        values={screen.form}
        onChange={screen.setForm}
        onSubmit={screen.submitEdit}
        saving={screen.savingEdit}
        showTitleError={screen.showTitleError}
        mode="edit"
      />
      <DeletePatientDialog
        row={screen.deleteOpen ? caseRow : null}
        onCancel={screen.closeDelete}
        onConfirm={screen.confirmDelete}
        loading={screen.deleting}
      />
    </div>
  );
}
